import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, FileSearch, Kanban, TrendingUp, MapPin, Building2, Sparkles, ArrowRight } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from "recharts";

const CHART_COLORS = ["#06B6D4", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899"];

export default function Dashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [topOpps, setTopOpps] = useState([]);

  useEffect(() => {
    (async () => {
      const [m, o] = await Promise.all([
        api.get("/dashboard/metrics"),
        api.get("/opportunities", { params: { min_score: 80 } }),
      ]);
      setMetrics(m.data);
      setTopOpps((o.data.items || []).slice(0, 5));
    })().catch(() => {});
  }, []);

  const greetings = () => {
    const h = new Date().getHours();
    if (h < 12) return "Bonjour";
    if (h < 18) return "Bon après-midi";
    return "Bonsoir";
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            {greetings()}, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-muted-foreground mt-1">Trouvons votre prochaine opportunité de contrat.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/opportunites"><Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950" data-testid="find-opportunities-cta"><Search className="h-4 w-4 mr-2" /> Trouver des opportunités</Button></Link>
          <Link to="/appels-offres"><Button variant="outline" data-testid="search-tenders-cta"><FileSearch className="h-4 w-4 mr-2" /> Voir les appels d'offres</Button></Link>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Opportunités détectées", value: metrics?.total_opportunities ?? "—", icon: Building2, color: "text-cyan-400" },
          { label: "Priorité haute", value: metrics?.high_priority ?? "—", icon: TrendingUp, color: "text-emerald-400" },
          { label: "Appels d'offres actifs", value: metrics?.active_tenders ?? "—", icon: FileSearch, color: "text-amber-400" },
          { label: "Leads dans mon CRM", value: metrics?.crm_leads_total ?? "—", icon: Kanban, color: "text-purple-400" },
        ].map((m, i) => (
          <Card key={i} className="p-5 bg-card border-border card-hover" data-testid={`metric-card-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{m.label}</span>
              <m.icon className={`h-4 w-4 ${m.color}`} />
            </div>
            <div className="font-display text-3xl font-bold">{m.value}</div>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 bg-card border-border lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Répartition géographique</h3>
            <Badge variant="outline" className="text-xs">Grand Montréal</Badge>
          </div>
          {metrics?.by_city?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={metrics.by_city}>
                <XAxis dataKey="city" tick={{ fill: "#94a3b8", fontSize: 12 }} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid #1E293B", borderRadius: 8 }} />
                <Bar dataKey="count" fill="#06B6D4" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-60 flex items-center justify-center text-muted-foreground text-sm">Aucune donnée</div>
          )}
        </Card>
        <Card className="p-6 bg-card border-border">
          <h3 className="font-display font-semibold mb-4">Par secteur</h3>
          {metrics?.by_industry?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={metrics.by_industry} dataKey="count" nameKey="industry" innerRadius={45} outerRadius={80}>
                  {metrics.by_industry.map((_, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                </Pie>
                <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid #1E293B", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-60 flex items-center justify-center text-muted-foreground text-sm">Aucune donnée</div>
          )}
        </Card>
      </div>

      {/* Top opportunities */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-cyan-400" /> Top opportunités selon l'IA</h3>
          <Link to="/opportunites" className="text-sm text-cyan-400 hover:underline flex items-center gap-1">Voir tout <ArrowRight className="h-3 w-3" /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {topOpps.map((o) => (
            <Link key={o.opportunity_id} to={`/opportunites/${o.opportunity_id}`} data-testid={`top-opp-${o.opportunity_id}`}>
              <Card className="p-5 bg-card border-border card-hover h-full">
                <div className="flex items-start justify-between mb-2">
                  <Badge className="bg-cyan-500/10 text-cyan-300 border-cyan-500/30 text-xs">{o.opportunity_type}</Badge>
                  <div className="text-right">
                    <div className="text-xl font-display font-bold text-emerald-400">{o.ai_score}</div>
                    <div className="text-[10px] font-mono uppercase text-muted-foreground">Score IA</div>
                  </div>
                </div>
                <h4 className="font-semibold mb-1 line-clamp-1">{o.company_name}</h4>
                <div className="text-xs text-muted-foreground flex items-center gap-1 mb-2"><MapPin className="h-3 w-3" /> {o.city} · {o.industry}</div>
                {o.estimated_value && <div className="text-xs text-cyan-300 font-medium">≈ {new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(o.estimated_value)} / an</div>}
              </Card>
            </Link>
          ))}
          {!topOpps.length && (
            <Card className="p-8 col-span-full text-center text-muted-foreground border-dashed">
              Aucune opportunité de priorité haute pour le moment.
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
